import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortfolioAuth } from '@/context/PortfolioAuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import axios from 'axios';
import {
  MessageCircle,
  Upload,
  FileText,
  Home,
  CheckCircle,
  Edit3,
  Send,
  RefreshCw,
  AlertCircle,
  Building2,
  Calendar,
  DollarSign,
  Users,
  MapPin,
  Loader2,
  X,
  ChevronRight,
  FileCheck,
  Plus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Session states
const SESSION_STATES = {
  WELCOME: 'WELCOME',
  ASK_DOC_UPLOAD: 'ASK_DOC_UPLOAD',
  PROCESSING: 'PROCESSING',
  SHOW_RESULTS: 'SHOW_RESULTS',
  EDIT_FIELDS: 'EDIT_FIELDS',
  MATCH_PROPERTY: 'MATCH_PROPERTY',
  CONFIRM_AND_SAVE: 'CONFIRM_AND_SAVE',
  DONE: 'DONE'
};

// Status badge colors
const STATUS_COLORS = {
  'Draft': 'bg-gray-500',
  'Needs Confirmation': 'bg-yellow-500',
  'Completed': 'bg-green-500'
};

// Document type icons
const DOC_TYPE_ICONS = {
  'ALTA': FileText,
  'DEED': FileCheck,
  'LEASE': Home,
  'OTHER': FileText
};

// Chat Message Component
const ChatMessage = ({ message, onQuickReply, onPropertySelect, onEditField, editingFields, setEditingFields, token }) => {
  const isUser = message.role === 'user';
  const isFileUpload = message.message_type === 'file_upload';
  const isExtractionResult = message.message_type === 'extraction_result';
  const isPropertyMatches = message.message_type === 'property_matches';
  
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`max-w-[85%] ${isUser ? 'order-2' : 'order-1'}`}>
        {/* Avatar */}
        <div className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
            isUser ? 'bg-blue-600' : 'bg-gradient-to-r from-amber-500 to-amber-600'
          }`}>
            {isUser ? (
              <span className="text-white text-sm font-medium">U</span>
            ) : (
              <MessageCircle className="w-4 h-4 text-white" />
            )}
          </div>
          
          {/* Message Content */}
          <div className={`rounded-2xl px-4 py-3 ${
            isUser 
              ? 'bg-blue-600 text-white' 
              : 'bg-white border border-gray-200 shadow-sm'
          }`}>
            {/* File Upload Message */}
            {isFileUpload && (
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                <span>{message.content}</span>
              </div>
            )}
            
            {/* Regular Text Message */}
            {!isFileUpload && !isExtractionResult && !isPropertyMatches && (
              <div className="whitespace-pre-wrap">{message.content}</div>
            )}
            
            {/* Extraction Results */}
            {isExtractionResult && message.metadata?.extraction && (
              <ExtractionResultCard 
                extraction={message.metadata.extraction}
                documentId={message.metadata.document_id}
                onEditField={onEditField}
                editingFields={editingFields}
                setEditingFields={setEditingFields}
              />
            )}
            
            {/* Property Matches */}
            {isPropertyMatches && (
              <PropertyMatchesCard 
                matches={message.metadata?.matches || []}
                extraction={message.metadata?.extraction}
                onSelect={onPropertySelect}
                token={token}
              />
            )}
            
            {/* Quick Replies */}
            {!isUser && message.metadata?.quick_replies?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {message.metadata.quick_replies.map((reply) => (
                  <Button
                    key={reply.id}
                    variant="outline"
                    size="sm"
                    onClick={() => onQuickReply(reply)}
                    className="text-sm border-amber-500 text-amber-700 hover:bg-amber-50"
                  >
                    {reply.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Extraction Result Card Component
const ExtractionResultCard = ({ extraction, documentId, onEditField, editingFields, setEditingFields }) => {
  const DocIcon = DOC_TYPE_ICONS[extraction.doc_type] || FileText;
  const confidencePercent = Math.round(extraction.confidence * 100);
  
  const handleFieldEdit = (field, value) => {
    setEditingFields(prev => ({ ...prev, [field]: value }));
  };
  
  const renderEditableField = (label, field, value, icon) => {
    const isEditing = editingFields.hasOwnProperty(field);
    const displayValue = isEditing ? editingFields[field] : value;
    
    return (
      <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
        <div className="flex items-center gap-2 text-gray-600">
          {icon}
          <span className="text-sm">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <Input
              value={displayValue}
              onChange={(e) => handleFieldEdit(field, e.target.value)}
              className="h-8 w-48 text-sm"
            />
          ) : (
            <span className="text-sm font-medium">{value || '-'}</span>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => {
              if (isEditing) {
                // Save edit
                onEditField(documentId, field, editingFields[field]);
                setEditingFields(prev => {
                  const newFields = { ...prev };
                  delete newFields[field];
                  return newFields;
                });
              } else {
                // Start editing
                handleFieldEdit(field, value || '');
              }
            }}
          >
            {isEditing ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Edit3 className="w-4 h-4 text-gray-400" />}
          </Button>
        </div>
      </div>
    );
  };
  
  return (
    <div className="space-y-4">
      <div className="whitespace-pre-wrap text-gray-700">{extraction.explanation || "Analysis complete! Here's what I found:"}</div>
      
      <Card className="border-amber-200 bg-amber-50/50">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DocIcon className="w-5 h-5 text-amber-600" />
              <CardTitle className="text-lg">
                {extraction.doc_type} Document
              </CardTitle>
            </div>
            <Badge className={`${confidencePercent >= 70 ? 'bg-green-500' : confidencePercent >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}>
              {confidencePercent}% Confidence
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          {/* Property Address */}
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Property Address
            </h4>
            <div className="bg-white rounded-lg p-3 space-y-1">
              {renderEditableField('Street', 'property_address.street', extraction.property_address?.street, <Home className="w-4 h-4" />)}
              {renderEditableField('City', 'property_address.city', extraction.property_address?.city, <Building2 className="w-4 h-4" />)}
              {renderEditableField('State', 'property_address.state', extraction.property_address?.state, <MapPin className="w-4 h-4" />)}
              {renderEditableField('ZIP', 'property_address.zip', extraction.property_address?.zip, <MapPin className="w-4 h-4" />)}
            </div>
          </div>
          
          {/* Document Details */}
          <div className="bg-white rounded-lg p-3 space-y-1">
            {extraction.closing_date && renderEditableField('Closing Date', 'closing_date', extraction.closing_date, <Calendar className="w-4 h-4" />)}
            {extraction.purchase_price && renderEditableField('Purchase Price', 'purchase_price', `$${extraction.purchase_price?.toLocaleString()}`, <DollarSign className="w-4 h-4" />)}
            {extraction.loan_amount && renderEditableField('Loan Amount', 'loan_amount', `$${extraction.loan_amount?.toLocaleString()}`, <DollarSign className="w-4 h-4" />)}
            {extraction.monthly_rent && renderEditableField('Monthly Rent', 'monthly_rent', `$${extraction.monthly_rent?.toLocaleString()}`, <DollarSign className="w-4 h-4" />)}
            {extraction.buyer_names?.length > 0 && renderEditableField('Buyer(s)', 'buyer_names', extraction.buyer_names.join(', '), <Users className="w-4 h-4" />)}
            {extraction.seller_names?.length > 0 && renderEditableField('Seller(s)', 'seller_names', extraction.seller_names.join(', '), <Users className="w-4 h-4" />)}
          </div>
          
          {/* Notes */}
          {extraction.notes && (
            <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
                <p className="text-sm text-yellow-800">{extraction.notes}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Property Matches Card Component
const PropertyMatchesCard = ({ matches, extraction, onSelect, onUpdateProperty, token }) => {
  const [showCreateNew, setShowCreateNew] = useState(false);
  const [expandedMatch, setExpandedMatch] = useState(null);
  const [existingPropertyData, setExistingPropertyData] = useState(null);
  const [loadingProperty, setLoadingProperty] = useState(false);
  const [selectedUpdates, setSelectedUpdates] = useState({});
  const [newAddress, setNewAddress] = useState({
    street: extraction?.property_address?.street || '',
    city: extraction?.property_address?.city || '',
    state: extraction?.property_address?.state || '',
    zip: extraction?.property_address?.zip || ''
  });
  
  // Fetch property details when a match is expanded
  const fetchPropertyDetails = async (propertyId) => {
    if (!token) return;
    setLoadingProperty(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/portfolio/properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExistingPropertyData(res.data);
      // Pre-select updates for fields that differ and have extracted values
      const updates = {};
      if (extraction?.purchase_price && extraction.purchase_price !== res.data.purchase_price) {
        updates.purchase_price = extraction.purchase_price;
      }
      if (extraction?.monthly_rent && extraction.monthly_rent !== res.data.monthly_rent) {
        updates.monthly_rent = extraction.monthly_rent;
      }
      if (extraction?.closing_date && extraction.closing_date !== res.data.purchase_date) {
        updates.purchase_date = extraction.closing_date;
      }
      setSelectedUpdates(updates);
    } catch (error) {
      console.error('Error fetching property:', error);
      toast.error('Failed to load property details');
    } finally {
      setLoadingProperty(false);
    }
  };
  
  const handleMatchClick = async (match) => {
    if (expandedMatch === match.property_id) {
      setExpandedMatch(null);
      setExistingPropertyData(null);
      setSelectedUpdates({});
    } else {
      setExpandedMatch(match.property_id);
      await fetchPropertyDetails(match.property_id);
    }
  };
  
  const toggleUpdate = (field, value) => {
    setSelectedUpdates(prev => {
      if (prev.hasOwnProperty(field)) {
        const newUpdates = { ...prev };
        delete newUpdates[field];
        return newUpdates;
      } else {
        return { ...prev, [field]: value };
      }
    });
  };
  
  const handleSelectWithUpdates = (propertyId) => {
    // Pass the property ID and any selected updates
    onSelect(propertyId, null, selectedUpdates);
  };
  
  // Comparison row component
  const ComparisonRow = ({ label, currentValue, extractedValue, field, icon }) => {
    const hasExtractedValue = extractedValue !== null && extractedValue !== undefined && extractedValue !== '';
    const isDifferent = hasExtractedValue && currentValue !== extractedValue;
    const isSelected = selectedUpdates.hasOwnProperty(field);
    
    // Format display values
    const formatValue = (val) => {
      if (val === null || val === undefined || val === '') return 'Not set';
      if (typeof val === 'number') return `$${val.toLocaleString()}`;
      return val;
    };
    
    return (
      <div className={`p-3 rounded-lg ${isDifferent ? 'bg-amber-50 border border-amber-200' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            {icon}
            {label}
          </div>
          {isDifferent && (
            <Button
              variant="outline"
              size="sm"
              className={`h-7 text-xs ${isSelected ? 'bg-amber-500 text-white border-amber-500' : 'border-amber-400 text-amber-600'}`}
              onClick={() => toggleUpdate(field, extractedValue)}
            >
              {isSelected ? '✓ Will Update' : 'Update'}
            </Button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-xs text-gray-500 block">Current</span>
            <span className="font-medium">{formatValue(currentValue)}</span>
          </div>
          {hasExtractedValue && (
            <div>
              <span className="text-xs text-gray-500 block">From Document</span>
              <span className={`font-medium ${isDifferent ? 'text-amber-600' : ''}`}>
                {formatValue(extractedValue)}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };
  
  return (
    <div className="space-y-4">
      <div className="whitespace-pre-wrap text-gray-700">
        {matches.length > 0 
          ? `I found ${matches.length} potential property match${matches.length > 1 ? 'es' : ''}. Click on a match to compare and optionally update with extracted data.`
          : "I couldn't find any existing properties matching this address. Would you like to create a new property?"}
      </div>
      
      {/* Existing Matches */}
      {matches.length > 0 && (
        <div className="space-y-3">
          {matches.map((match) => (
            <Card 
              key={match.property_id}
              className={`transition-all ${expandedMatch === match.property_id ? 'border-amber-500 shadow-md' : 'hover:border-amber-300'}`}
            >
              <CardContent className="p-4">
                {/* Match Header - Clickable */}
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => handleMatchClick(match)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Home className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium">{match.address}</p>
                      <p className="text-sm text-gray-500">
                        {Math.round(match.match_score * 100)}% match • {match.match_type}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${expandedMatch === match.property_id ? 'rotate-90' : ''}`} />
                </div>
                
                {/* Expanded Comparison View */}
                {expandedMatch === match.property_id && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    {loadingProperty ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
                        <span className="ml-2 text-gray-600">Loading property details...</span>
                      </div>
                    ) : existingPropertyData ? (
                      <div className="space-y-3">
                        <h4 className="font-medium text-gray-800 flex items-center gap-2">
                          <Edit3 className="w-4 h-4" /> Compare & Update
                        </h4>
                        <p className="text-sm text-gray-600">
                          Select which fields to update with the extracted data:
                        </p>
                        
                        {/* Comparison Fields */}
                        <div className="space-y-2">
                          <ComparisonRow
                            label="Purchase Price"
                            currentValue={existingPropertyData.purchase_price}
                            extractedValue={extraction?.purchase_price}
                            field="purchase_price"
                            icon={<DollarSign className="w-4 h-4" />}
                          />
                          <ComparisonRow
                            label="Monthly Rent"
                            currentValue={existingPropertyData.monthly_rent}
                            extractedValue={extraction?.monthly_rent}
                            field="monthly_rent"
                            icon={<DollarSign className="w-4 h-4" />}
                          />
                          <ComparisonRow
                            label="Purchase/Closing Date"
                            currentValue={existingPropertyData.purchase_date}
                            extractedValue={extraction?.closing_date}
                            field="purchase_date"
                            icon={<Calendar className="w-4 h-4" />}
                          />
                          {extraction?.loan_amount && (
                            <ComparisonRow
                              label="Loan Amount"
                              currentValue={existingPropertyData.mortgage_principal}
                              extractedValue={extraction?.loan_amount}
                              field="mortgage_principal"
                              icon={<DollarSign className="w-4 h-4" />}
                            />
                          )}
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex gap-2 pt-3">
                          <Button
                            className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                            onClick={() => handleSelectWithUpdates(match.property_id)}
                          >
                            {Object.keys(selectedUpdates).length > 0 
                              ? `Select & Update ${Object.keys(selectedUpdates).length} Field(s)`
                              : 'Select Property (No Updates)'}
                          </Button>
                        </div>
                        
                        {Object.keys(selectedUpdates).length > 0 && (
                          <p className="text-xs text-amber-600 text-center">
                            The selected fields will be updated when you confirm the intake.
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">Unable to load property details</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Create New Property */}
      <Card className="border-dashed border-2 border-gray-300 hover:border-amber-500 transition-colors">
        <CardContent className="p-4">
          {!showCreateNew ? (
            <button
              className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-amber-600"
              onClick={() => setShowCreateNew(true)}
            >
              <Plus className="w-5 h-5" />
              <span>Create New Property</span>
            </button>
          ) : (
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <Plus className="w-4 h-4" /> New Property Address
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs">Street</Label>
                  <Input 
                    value={newAddress.street}
                    onChange={(e) => setNewAddress(prev => ({ ...prev, street: e.target.value }))}
                    placeholder="123 Main St"
                  />
                </div>
                <div>
                  <Label className="text-xs">City</Label>
                  <Input 
                    value={newAddress.city}
                    onChange={(e) => setNewAddress(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="Orlando"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">State</Label>
                    <Input 
                      value={newAddress.state}
                      onChange={(e) => setNewAddress(prev => ({ ...prev, state: e.target.value }))}
                      placeholder="FL"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">ZIP</Label>
                    <Input 
                      value={newAddress.zip}
                      onChange={(e) => setNewAddress(prev => ({ ...prev, zip: e.target.value }))}
                      placeholder="32801"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCreateNew(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700"
                  onClick={() => onSelect(null, newAddress)}
                >
                  Create Property
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// File Upload Zone Component
const FileUploadZone = ({ onFileSelect, isUploading, uploadProgress }) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      onFileSelect(files[0]);
    }
  };
  
  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      onFileSelect(files[0]);
    }
  };
  
  return (
    <div
      className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
        isDragging 
          ? 'border-amber-500 bg-amber-50' 
          : 'border-gray-300 hover:border-amber-400 hover:bg-amber-50/50'
      } ${isUploading ? 'pointer-events-none opacity-50' : 'cursor-pointer'}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => !isUploading && fileInputRef.current?.click()}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".pdf,.jpg,.jpeg,.png,image/*"
        className="hidden"
        multiple
      />
      
      {isUploading ? (
        <div className="space-y-4">
          <Loader2 className="w-12 h-12 mx-auto text-amber-600 animate-spin" />
          <div className="space-y-2">
            <p className="text-gray-600">Uploading and analyzing document...</p>
            <Progress value={uploadProgress} className="w-64 mx-auto" />
            <p className="text-sm text-gray-500">{uploadProgress}%</p>
          </div>
        </div>
      ) : (
        <>
          <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-2">
            Drop your document here
          </p>
          <p className="text-sm text-gray-500 mb-2">
            or tap to select files
          </p>
          <p className="text-xs text-gray-400 mb-4">
            Supported: PDF, JPG, PNG
          </p>
          <div className="flex justify-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-xs">PDF</Badge>
            <Badge variant="outline" className="text-xs">JPG</Badge>
            <Badge variant="outline" className="text-xs">PNG</Badge>
            <Badge variant="outline" className="text-xs">Photos</Badge>
            <Badge variant="outline" className="text-xs">Camera</Badge>
          </div>
        </>
      )}
    </div>
  );
};

// Main Chat Agent Component
const ChatAgent = () => {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading: authLoading } = usePortfolioAuth();
  const messagesEndRef = useRef(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editingFields, setEditingFields] = useState({});
  const [inputMessage, setInputMessage] = useState('');
  const [currentDocumentId, setCurrentDocumentId] = useState(null);
  
  // Check if authenticated (isAuthenticated is a function)
  const isAuthed = isAuthenticated();
  
  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [session?.messages]);
  
  // Check authentication
  useEffect(() => {
    if (!authLoading && !isAuthed) {
      toast.error('Please log in to access the Document Intake Agent');
      navigate('/portfolio/login');
    }
  }, [authLoading, isAuthed, navigate]);
  
  // Load or create session
  const loadOrCreateSession = useCallback(async () => {
    if (!token) {
      console.log('No token available, skipping session load');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      console.log('Loading chat session with token:', token.substring(0, 20) + '...');
      
      // Check for existing draft session
      const sessionsRes = await axios.get(`${API}/chat/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status: 'Draft', limit: 1 }
      });
      
      console.log('Sessions response:', sessionsRes.data);
      
      if (sessionsRes.data.sessions?.length > 0) {
        setSession(sessionsRes.data.sessions[0]);
      } else {
        // Create new session
        console.log('Creating new chat session...');
        const createRes = await axios.post(`${API}/chat/sessions`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('New session created:', createRes.data);
        setSession(createRes.data.session);
      }
    } catch (error) {
      console.error('Error loading session:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to load chat session. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  useEffect(() => {
    if (isAuthed && token) {
      loadOrCreateSession();
    }
  }, [isAuthed, token, loadOrCreateSession]);
  
  const handleQuickReply = async (reply) => {
    if (!session || !token) return;
    
    try {
      const action = reply.action || reply.value || reply.id;
      
      // Handle special actions
      if (action === 'restart' || action === 'new_session') {
        const res = await axios.post(`${API}/chat/sessions/${session.id}/restart`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSession(res.data.session);
        return;
      }
      
      // Handle "create_new" property action - need to call select-property endpoint
      if (action === 'create_new') {
        // Get the extraction from the latest message with extraction data
        const extractionMessage = [...session.messages].reverse().find(
          m => m.metadata?.extraction?.property_address
        );
        
        if (extractionMessage?.metadata?.extraction?.property_address) {
          const addr = extractionMessage.metadata.extraction.property_address;
          await handlePropertySelect(null, addr);
        } else {
          toast.error('No address found. Please edit the extracted fields first.');
        }
        return;
      }
      
      // Handle "confirm" action - call confirm endpoint
      if (action === 'confirm') {
        await handleConfirm();
        return;
      }
      
      // Send message with quick reply
      const res = await axios.post(`${API}/chat/sessions/${session.id}/message`, {
        content: reply.label,
        message_type: 'quick_reply',
        metadata: {
          action: action,
          doc_type: reply.value
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSession(res.data.session);
      
      // After showing results, auto-trigger property matching if user clicks "continue"
      if (action === 'continue') {
        await handleMatchProperties();
      }
    } catch (error) {
      console.error('Error handling quick reply:', error);
      toast.error('Failed to process action');
    }
  };
  
const handleFileUpload = async (file) => {
  if (!session || !token) {
    toast.error('Please log in to upload files');
    return;
  }
  
  try {
    setIsUploading(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append('session_id', session.id);
    formData.append('file', file);
    formData.append('direct_upload', 'true');
    
    // Upload the file
    const res = await axios.post(`${API}/chat/documents/intake`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(Math.min(percent, 90));
      }
    });
    
    setUploadProgress(100);
    
    // Force a complete re-render with the new session data
    if (res.data?.session) {
      // Create a new session object to ensure React detects the change
      const updatedSession = { ...res.data.session };
      
      // Ensure messages is an array
      if (!Array.isArray(updatedSession.messages)) {
        updatedSession.messages = [];
      }
      
      setSession(updatedSession);
      
      // If we have document intake data, update the current document ID
      if (res.data.document_intake?.id) {
        setCurrentDocumentId(res.data.document_intake.id);
      }
      
      // Handle extraction results if available
      if (res.data.document_intake?.extraction) {
        const { extraction } = res.data.document_intake;
        
        // For high confidence, automatically set document type
        if (extraction.confidence > 0.8) {
          try {
            const docTypeRes = await axios.post(
              `${API}/chat/sessions/${session.id}/set-document-type`,
              { doc_type: extraction.doc_type },
              { 
                headers: { 
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json'
                } 
              }
            );
            
            // Update session with the latest data
            if (docTypeRes.data?.session) {
              setSession({ ...docTypeRes.data.session });
            }
            
            toast.success(`Detected ${extraction.doc_type} document with high confidence`);
          } catch (error) {
            console.error('Error setting document type:', error);
            // Continue even if setting doc type fails
          }
        } else {
          toast.success('Document analyzed! Please review the extracted information.');
        }
      }
    }
    
    // Force scroll to bottom after state updates
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    
  } catch (error) {
    console.error('Error uploading file:', error);
    const errorMessage = error.response?.data?.detail || 'Failed to upload document';
    toast.error(errorMessage);
    
    // If session becomes invalid, force a refresh
    if (error.response?.status === 401 || error.response?.status === 403) {
      window.location.reload();
    }
  } finally {
    setIsUploading(false);
    setUploadProgress(0);
  }
};
  const handleEditField = async (documentId, field, value) => {
    if (!token) return;
    
    try {
      await axios.put(`${API}/chat/documents/intake/${documentId}`, {
        document_intake_id: documentId,
        updates: { [field]: value }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Field updated');
    } catch (error) {
      console.error('Error updating field:', error);
      toast.error('Failed to update field');
    }
  };
  
  const handleMatchProperties = async () => {
    if (!session || !token) return;
    
    try {
      const res = await axios.post(`${API}/chat/sessions/${session.id}/match-properties`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSession(res.data.session);
    } catch (error) {
      console.error('Error matching properties:', error);
      toast.error('Failed to match properties');
    }
  };
  
  const handlePropertySelect = async (propertyId, newAddress = null, propertyUpdates = null) => {
    if (!session || !token) return;
    
    try {
      // Validate new address has at least street
      if (!propertyId && newAddress) {
        if (!newAddress.street || newAddress.street.trim() === '') {
          toast.error('Please enter at least a street address');
          return;
        }
      }
      
      const payload = propertyId 
        ? { property_id: propertyId, property_updates: propertyUpdates }
        : { new_property_address: newAddress };
      
      const res = await axios.post(`${API}/chat/sessions/${session.id}/select-property`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSession(res.data.session);
      
      if (propertyId && propertyUpdates && Object.keys(propertyUpdates).length > 0) {
        toast.success(`Property selected and ${Object.keys(propertyUpdates).length} field(s) will be updated`);
      } else {
        toast.success(propertyId ? 'Property selected' : 'New property will be created');
      }
    } catch (error) {
      console.error('Error selecting property:', error);
      toast.error(error.response?.data?.detail || 'Failed to select property');
    }
  };
  
  const handleConfirm = async () => {
    if (!session || !token) return;
    
    try {
      setLoading(true);
      
      const formData = new FormData();
      formData.append('session_id', session.id);
      
      const res = await axios.post(`${API}/chat/documents/confirm`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSession(res.data.session);
      toast.success('Documents saved successfully!');
    } catch (error) {
      console.error('Error confirming intake:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm intake');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !session || !token) return;
    
    try {
      const res = await axios.post(`${API}/chat/sessions/${session.id}/message`, {
        content: inputMessage,
        message_type: 'text',
        metadata: {}
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSession(res.data.session);
      setInputMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };
  
  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }
  
  // Not authenticated - redirect will happen in useEffect
  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-gray-600">Redirecting to login...</p>
        </div>
      </div>
    );
  }
  
  // Loading session
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading Document Intake Agent...</p>
        </div>
      </div>
    );
  }
  
  const showFileUpload = session?.state === SESSION_STATES.ASK_DOC_UPLOAD || 
                         session?.state === SESSION_STATES.WELCOME;
  const showConfirmButton = session?.state === SESSION_STATES.CONFIRM_AND_SAVE;
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Document Intake Agent</h1>
                <p className="text-sm text-gray-500">Upload and classify real estate documents</p>
              </div>
            </div>
            
            {/* Session Status Badge */}
            {session && (
              <Badge className={`${STATUS_COLORS[session.status]} text-white`}>
                {session.status}
              </Badge>
            )}
          </div>
        </div>
      </div>
      
      {/* Chat Container */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Card className="min-h-[70vh] flex flex-col">
          {/* Messages Area */}
          <CardContent className="flex-1 overflow-y-auto p-6 space-y-4">
            {session?.messages?.map((message, index) => (
              <ChatMessage
                key={message.id || index}
                message={message}
                onQuickReply={handleQuickReply}
                onPropertySelect={handlePropertySelect}
                onEditField={handleEditField}
                editingFields={editingFields}
                setEditingFields={setEditingFields}
                token={token}
              />
            ))}
            
            {/* File Upload Zone */}
            {showFileUpload && (
              <div className="mt-4">
                <FileUploadZone 
                  onFileSelect={handleFileUpload}
                  isUploading={isUploading}
                  uploadProgress={uploadProgress}
                />
              </div>
            )}
            
            {/* Confirm Button */}
            {showConfirmButton && (
              <div className="mt-4 flex justify-center">
                <Button
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white px-8"
                  onClick={handleConfirm}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Confirm & Save
                    </>
                  )}
                </Button>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </CardContent>
          
          {/* Input Area */}
          <Separator />
          <div className="p-4">
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1"
              />
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </Card>
        
        {/* Session Actions */}
        {session && session.state !== SESSION_STATES.WELCOME && (
          <div className="mt-4 flex justify-center gap-4">
            <Button
              variant="outline"
              onClick={async () => {
                const res = await axios.post(`${API}/chat/sessions/${session.id}/restart`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                setSession(res.data.session);
              }}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Upload Another Document
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                const res = await axios.post(`${API}/chat/sessions`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                setSession(res.data.session);
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              New Session
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatAgent;
